import React, { useEffect } from 'react';
import _ from 'lodash';
import { ListRowProps } from 'react-virtualized';
import { useIsMobile } from 'components/Page/useIsMobile';
import { VirtualizedList } from 'components/Table/VirtualizedList';
import { makeStyles } from '@material-ui/core/styles';
import { MenuItem, TextField, Tooltip, Typography } from '@material-ui/core';
import NumberFormat from 'react-number-format';
import { NavLink } from 'react-router-dom';
import { useLoadMoreNearEnd } from 'components/Table/useLoadMoreNearEnd';
import { AocClasses } from '@pvplevelup/core/dist/interfaces/ICharacter';
import { classImages } from 'utilities/ClassImages';
import { ClassColorsObject } from 'utilities/team_colors';
import { VirtualizedTable } from 'components/Table/VirtualizedTable';
import { usePageStyles } from 'components/Page/PageKit';
import PlaceHolderImage from 'assets/images/icons/no_profile.jpg';

// note: Electron 8 (Chromium 80) has no flexbox gap; spacing here is grid gap or margins

const WITH_COLOR = 'rgb(62, 124, 255)';
const AGAINST_COLOR = 'rgb(228, 54, 55)';
const ALL_COLOR = 'rgba(255,255,255,0.7)';
const GOOD = '#66bb6a';
const BAD = '#ef5350';

const useStyles = makeStyles((theme) => ({
  root: {
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
  },
  toolbar: {
    flexShrink: 0,
    display: 'grid',
    gridTemplateColumns: '200px 1fr',
    alignItems: 'center',
    gridGap: theme.spacing(3),
    marginBottom: theme.spacing(1.5),
  },
  legend: {
    display: 'grid',
    gridAutoFlow: 'column',
    justifyContent: 'start',
    alignItems: 'center',
    gridGap: theme.spacing(2.5),
    fontSize: '0.85rem',
    opacity: 0.85,
  },
  dot: {
    display: 'inline-block',
    width: 8,
    height: 8,
    borderRadius: '50%',
    marginRight: 6,
    verticalAlign: 'middle',
  },
  person: {
    display: 'grid',
    gridTemplateColumns: '32px minmax(0, 1fr)',
    gridGap: theme.spacing(1.25),
    alignItems: 'center',
    width: '100%',
  },
  icon: {
    width: 32,
    height: 32,
    objectFit: 'cover',
  },
  link: {
    color: '#fff',
    fontWeight: 600,
    textDecoration: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    display: 'block',
    '&:hover': {
      textDecoration: 'underline',
    },
  },
  sub: {
    fontSize: '0.78rem',
  },
  values: {
    display: 'grid',
    gridAutoFlow: 'column',
    gridAutoColumns: '1fr',
    alignItems: 'center',
    width: '100%',
  },
  value: {
    whiteSpace: 'nowrap',
    fontWeight: 600,
  },
  // phone layout: the toolbar stacks, and each person is a card with a small with / all / against grid
  mToolbar: {
    flexShrink: 0,
    marginBottom: theme.spacing(1),
  },
  mLegend: {
    display: 'flex',
    flexWrap: 'wrap',
    fontSize: '0.8rem',
    opacity: 0.85,
    marginTop: theme.spacing(1),
    '& > span': {
      marginRight: theme.spacing(1.5),
    },
  },
  mCard: {
    padding: theme.spacing(1.25),
    marginBottom: theme.spacing(1),
    borderRadius: 8,
    background: 'rgba(20, 20, 20, 0.92)',
    border: '1px solid rgba(255, 255, 255, 0.1)',
  },
  mGrid: {
    display: 'grid',
    gridTemplateColumns: 'minmax(0, 1.2fr) repeat(3, minmax(0, 1fr))',
    gridRowGap: 4,
    marginTop: theme.spacing(1),
    paddingTop: theme.spacing(0.75),
    borderTop: '1px solid rgba(255,255,255,0.06)',
    fontSize: '0.85rem',
    '& > *': {
      textAlign: 'right',
      whiteSpace: 'nowrap',
    },
    '& > *:nth-child(4n+1)': {
      textAlign: 'left',
      opacity: 0.65,
    },
  },
  mHead: {
    fontSize: '0.68rem',
    fontWeight: 700,
    letterSpacing: '0.06em',
    textTransform: 'uppercase',
  },
}));

const Dot: React.FC<{color: string}> = ({color}) => {
  const classes = useStyles();
  return <span className={classes.dot} style={{background: color}} />;
}

/** One number with the dot saying whether it's with, all, or against. */
const Value: React.FC<{dot: string, color?: string, value: number, decimals: number, suffix?: string}> = ({dot, color, value, decimals, suffix}) => {
  const classes = useStyles();
  return (
    <span className={classes.value} style={{color}}>
      <Dot color={dot} />
      <NumberFormat value={value} decimalScale={decimals} displayType="text" />{suffix}
    </span>
  );
}

const better = (value: number, even: number) => value > even ? GOOD : value < even ? BAD : undefined;

const num = (value: number, decimals: number, color?: string, suffix?: string) =>
  <span style={{color, fontWeight: 600}}><NumberFormat value={value} decimalScale={decimals} displayType="text" />{suffix}</span>;

/** The phone version of a played-with row. */
const MobilePlayedWithRow: React.FC<{pwStat: any, byCharacter: boolean, measure: () => void}> = ({pwStat, byCharacter, measure}) => {
  const classes = useStyles();
  useEffect(() => { measure(); }, []);
  const withDiff = (pwStat.with?.kills || 0) - (pwStat.with?.deaths || 0);
  const againstDiff = (pwStat.against?.kills || 0) - (pwStat.against?.deaths || 0);
  const withKd = pwStat.with?.kd || 0;
  const againstKd = pwStat.against?.kd || 0;
  const avgWithKd = pwStat.with?.game_kd || 0;
  const avgAgainstKd = pwStat.against?.game_kd || 0;
  const pwClass = pwStat.playedWith ? pwStat.playedWith.class : "";
  const none = <span style={{opacity: 0.3}}>-</span>;
  return (
    <div style={{paddingBottom: 1}}>
      <div className={classes.mCard}>
        <div className={classes.person}>
          <img className={classes.icon} style={byCharacter ? {} : {borderRadius: '50%'}}
            src={byCharacter ? classImages.getColorIcon(pwClass as AocClasses) : (pwStat.playedWithUser?.avatar || PlaceHolderImage)} />
          <div style={{minWidth: 0}}>
            <NavLink to={byCharacter ? `/characters/${pwStat.playedWith?._id}` : `/users/${pwStat.playedWithUser?._id}`} className={classes.link}>
              {byCharacter ? pwStat.playedWith?.name : pwStat.playedWithUser?.displayname}
            </NavLink>
            {byCharacter && pwClass && <div className={classes.sub} style={{color: (ClassColorsObject as Record<string, string>)[pwClass]}}>{pwClass}</div>}
          </div>
        </div>
        <div className={classes.mGrid}>
          <span />
          <span className={classes.mHead} style={{color: WITH_COLOR}}>With</span>
          <span className={classes.mHead}>All</span>
          <span className={classes.mHead} style={{color: AGAINST_COLOR}}>Against</span>

          <span>Games</span>
          {num((pwStat.with?.wins || 0) + (pwStat.with?.loses || 0), 0)}
          {num(pwStat.played || 0, 0)}
          {num((pwStat.against?.wins || 0) + (pwStat.against?.loses || 0), 0)}

          <span>Win rate</span>
          {num((pwStat.with?.wr || 0) * 100, 0, better(pwStat.with?.wr || 0, 0.5), '%')}
          {num((pwStat.wr || 0) * 100, 0, better(pwStat.wr || 0, 0.5), '%')}
          {num((pwStat.against?.wr || 0) * 100, 0, better(pwStat.against?.wr || 0, 0.5), '%')}

          <span>K/D</span>
          {num(withKd, 2, better(withKd, 1))}
          {none}
          {num(againstKd, 2, better(againstKd, 1))}

          <span>+/-</span>
          {num(withDiff, 0, better(withDiff, 0))}
          {none}
          {num(againstDiff, 0, better(againstDiff, 0))}

          <span>Avg K/D</span>
          {num(avgWithKd, 2, better(avgWithKd, 1))}
          {none}
          {num(avgAgainstKd, 2, better(avgAgainstKd, 1))}
        </div>
      </div>
    </div>
  );
}

interface Props {
  playedWithFeed: any;
  fetchMoreRecords: () => void;
  byCharacter: boolean;
  onChangeByCharacter: (byCharacter: boolean) => void;
}

/** The Played With tab of the character and user pages: how they do with and against everyone they've met. */
export const ProfilePlayedWith: React.FC<Props> = ({playedWithFeed, fetchMoreRecords, byCharacter, onChangeByCharacter}) => {
  const classes = useStyles();
  const page = usePageStyles();
  const stats: any[] = playedWithFeed?.stats || [];
  const first = stats[0];
  const onRowsRendered = useLoadMoreNearEnd(stats.length, playedWithFeed?.hasNext, fetchMoreRecords,
    first && `${byCharacter}-${first.playedWith?._id || first.playedWithUser?._id}`);
  const mobile = useIsMobile();

  if(mobile)
    return (
      <div className={classes.root}>
        <div className={classes.mToolbar}>
          <TextField select size="small" variant="outlined" label="Show" fullWidth value={byCharacter ? 0 : 1}
            onChange={(event) => onChangeByCharacter(Number(event.target.value) == 0)}>
            <MenuItem value={0}>By character</MenuItem>
            <MenuItem value={1}>By player</MenuItem>
          </TextField>
          <div className={classes.mLegend}>
            <span><Dot color={WITH_COLOR} />With = same team</span>
            <span><Dot color={AGAINST_COLOR} />Against = other team</span>
          </div>
        </div>
        <div style={{flex: '1 1 auto', minHeight: 0, position: 'relative'}}>
          <VirtualizedList
            // the cards differ between by character and by player, so they're measured again
            key={byCharacter ? 'character' : 'player'}
            data={stats}
            overscanRowCount={5}
            onRowsRendered={onRowsRendered}
            customRenderer={(props: ListRowProps, pwStat: any, measure: () => void) =>
              <MobilePlayedWithRow pwStat={pwStat} byCharacter={byCharacter} measure={measure} />
            }
          />
          {playedWithFeed && stats.length == 0 &&
            <div className={page.empty} style={{position: 'absolute', top: 0, left: 0, right: 0}}>
              <Typography>Nobody to show with these filters.</Typography>
            </div>
          }
        </div>
      </div>
    );

  return (
    <div className={classes.root}>
      <div className={classes.toolbar}>
        <TextField select size="small" variant="outlined" label="Show" value={byCharacter ? 0 : 1}
          onChange={(event) => onChangeByCharacter(Number(event.target.value) == 0)}>
          <MenuItem value={0}>By character</MenuItem>
          <MenuItem value={1}>By player</MenuItem>
        </TextField>
        <div className={classes.legend}>
          <span><Dot color={WITH_COLOR} />With them (same team)</span>
          <span><Dot color={ALL_COLOR} />All games</span>
          <span><Dot color={AGAINST_COLOR} />Against them</span>
        </div>
      </div>
      <div className={page.tablePanel} style={{flex: '1 1 auto', minHeight: 0}}>
        <VirtualizedTable
          data={stats}
          minWidth={980}
          rowHeight={56}
          headerHeight={40}
          rowCount={stats.length}
          rowGetter={({ index }) => stats[index]}
          onRowsRendered={onRowsRendered}
          overscanRowCount={5}
          customCellRenderer={({columnIndex, rowIndex, rowData}) => {
            const pwStat = rowData;
            const withDiff = (pwStat.with?.kills || 0) - (pwStat.with?.deaths || 0);
            const againstDiff = (pwStat.against?.kills || 0) - (pwStat.against?.deaths || 0);
            const withKd = pwStat.with?.kd || 0;
            const againstKd = pwStat.against?.kd || 0;
            const avgWithDiff = _.round((pwStat.with?.avg_kills || 0) - (pwStat.with?.avg_deaths || 0), 0);
            const avgAgainstDiff = _.round((pwStat.against?.avg_kills || 0) - (pwStat.against?.avg_deaths || 0));
            const avgAgainstKd = pwStat.against?.game_kd || 0;
            const avgWithKd = pwStat.with?.game_kd || 0;
            const pwClass = pwStat.playedWith ? pwStat.playedWith.class : "";
            return (<React.Fragment>
              {columnIndex == 0 ?
                <div className={classes.person}>
                  <img className={classes.icon} style={byCharacter ? {} : {borderRadius: '50%'}}
                    src={byCharacter ? classImages.getColorIcon(pwClass as AocClasses) : (pwStat.playedWithUser?.avatar || PlaceHolderImage)} title={byCharacter ? pwClass : ""} />
                  <div style={{minWidth: 0}}>
                    <NavLink to={byCharacter ? `/characters/${pwStat.playedWith?._id}` : `/users/${pwStat.playedWithUser?._id}`} className={classes.link}>
                      {byCharacter ? pwStat.playedWith?.name : pwStat.playedWithUser?.displayname}
                    </NavLink>
                    {byCharacter && pwClass && <div className={classes.sub} style={{color: (ClassColorsObject as Record<string, string>)[pwClass]}}>{pwClass}</div>}
                  </div>
                </div>
              : columnIndex == 1 ?
                <div className={classes.values}>
                  <Value dot={WITH_COLOR} value={(pwStat.with?.wins || 0) + (pwStat.with?.loses || 0)} decimals={0} />
                  <Value dot={ALL_COLOR} value={pwStat.played || 0} decimals={0} />
                  <Value dot={AGAINST_COLOR} value={(pwStat.against?.wins || 0) + (pwStat.against?.loses || 0)} decimals={0} />
                </div>
              : columnIndex == 2 ?
                <div className={classes.values}>
                  <Value dot={WITH_COLOR} color={better(pwStat.with?.wr || 0, 0.5)} value={(pwStat.with?.wr || 0) * 100} decimals={0} suffix="%" />
                  <Value dot={ALL_COLOR} color={better(pwStat.wr || 0, 0.5)} value={(pwStat.wr || 0) * 100} decimals={0} suffix="%" />
                  <Value dot={AGAINST_COLOR} color={better(pwStat.against?.wr || 0, 0.5)} value={(pwStat.against?.wr || 0) * 100} decimals={0} suffix="%" />
                </div>
              : columnIndex == 3 ?
                <div className={classes.values}>
                  <Value dot={WITH_COLOR} color={better(withKd, 1)} value={withKd} decimals={2} />
                  <Value dot={AGAINST_COLOR} color={better(againstKd, 1)} value={againstKd} decimals={2} />
                </div>
              : columnIndex == 4 ?
                <div className={classes.values}>
                  <Value dot={WITH_COLOR} color={better(withDiff, 0)} value={withDiff} decimals={0} />
                  <Value dot={AGAINST_COLOR} color={better(againstDiff, 0)} value={againstDiff} decimals={0} />
                </div>
              : columnIndex == 5 ?
                <div className={classes.values}>
                  <Value dot={WITH_COLOR} color={better(avgWithKd, 1)} value={avgWithKd} decimals={2} />
                  <Value dot={AGAINST_COLOR} color={better(avgAgainstKd, 1)} value={avgAgainstKd} decimals={2} />
                </div>
              : columnIndex == 6 ?
                <div className={classes.values}>
                  <Value dot={WITH_COLOR} color={better(avgWithDiff, 0)} value={avgWithDiff} decimals={0} />
                  <Value dot={AGAINST_COLOR} color={better(avgAgainstDiff, 0)} value={avgAgainstDiff} decimals={0} />
                </div>
              : null
              }
            </React.Fragment>)
          }}
          columns={[
            {width: 200, label: byCharacter ? 'Character' : 'Player', dataKey: 'played'},
            {width: 170, label: 'Games', dataKey: 'played'},
            {width: 190, label: 'Win Rate', dataKey: 'wr'},
            {width: 130, label: 'K/D', dataKey: 'kd'},
            {width: 120, label: '+/-', dataKey: 'diff'},
            {width: 130, label: 'Avg K/D', dataKey: 'avgkd'},
            {width: 120, label: 'Avg +/-', dataKey: 'avgdiff'},
          ]}
        />
        {playedWithFeed && stats.length == 0 &&
          <div className={page.empty} style={{position: 'absolute', top: 40, left: 0, right: 0}}>
            <Typography>Nobody to show with these filters.</Typography>
          </div>
        }
      </div>
    </div>
  );
}
